import { Record as record, Map, Set } from 'immutable';
import joi from 'joi';

import { sum } from '../../helpers/Immutable';
import ValidationMixin from '../ValidationMixin';
import validators from '../../validation';
import { toPounds } from '../../helpers/Currency';

const defaults = {
    note: null,
    reason: null,
    fares: new Map(),
    voucherAmount: 0,
    feePaid: false,
    maxCashReturn: 0,
    plusBus: new Map(),
    isSmartcard: false,
    itsoAnnualGoldCardAccepted: false, // Checkbox for annual customers only, that have a Goldcard
    shouldSurrenderGoldCard: false,
    itsoWMCAccepted: false, // Checkbox for weekly, monthly and custom customers
    isWMC: false,
    totalUsed: 0,
    bookingFee: 0,
    googlePayAmount: 0,
    applePayAmount: 0,
};

class RefundRequest extends record(defaults) {

    getComputedValues() {
        return {
            reason: this.reasonCode,
            tickets: this.tickets.size,
            total: this.total,
            totalUsed: this.totalUsed,
            itsoAnnualGoldCardAccepted: this.itsoAnnualGoldCardAccepted,
            itsoWMCAccepted: this.itsoWMCAccepted
        };
    }

    getSchema() {
        return {
            note: this.getIn(['reason', 'moreDetailsRequired'], false) ? validators.requiredString(1, 300) : joi.any(),
            reason: !this.isSmartcard ? joi.any().required() : joi.any(),
            tickets: joi.number().min(1),
            total: joi.number().min(1),
            totalUsed: joi.number(),
            itsoAnnualGoldCardAccepted: this.isSmartcard && this.shouldSurrenderGoldCard ? validators.true().required() : joi.boolean(),
            itsoWMCAccepted: this.isSmartcard && this.isWMC ? validators.true().required() : joi.boolean()
        };
    }

    hasValidSelection (trip) {
        const fares = this.fares.keySeq().toSet();
        return !!fares.size && fares.every((fare) => !this.getFareWarnings(fare, trip));
    }

    hasChildFlatFareWarning(fare, selectedTickets, remainingTickets) {
        const hasChildFlatFareTickets =
            fare && fare.tickets && fare.tickets.some(ticket => ticket.isChildFlatFare());

        const hasSelectedTickets = selectedTickets && selectedTickets.size > 0;

        if (!hasSelectedTickets || !hasChildFlatFareTickets) {
            return false;
        }

        const numRemainingAdultTickets = remainingTickets.reduce(
            (total, ticket) => total + (ticket.adults ? 1 : 0),
            0,
        );

        const numRemainingChildFlatTickets = remainingTickets.reduce(
            (total, ticket) => total + (ticket.isChildFlatFare() ? 1 : 0),
            0,
        );

        const hasRemainingAdultTickets = numRemainingAdultTickets > 0;

        const hasRemainingChildFlatTickets = numRemainingChildFlatTickets > 0;

        // Check if there are any remaining child flat tickets left to select
        // before issuing a warning (crucial for GroupSave to work)
        return !hasRemainingAdultTickets && hasRemainingChildFlatTickets;
    }

    hasNotSelectedAllChildren(fare, selectedTickets, remainingTickets) {
        const hasChildTickets = fare && fare.tickets && fare.tickets.some(ticket => ticket.isChildTicket());
        const hasSelectedTickets = selectedTickets && selectedTickets.size > 0;
        const hasSelectedAdultTicket = hasSelectedTickets ? selectedTickets.some(ticket => ticket.adults) : false;
        const numRemainingChildTickets = remainingTickets.reduce(
            (total, ticket) => total + (ticket.isChildTicket() ? 1 : 0),
            0,
        );
        const hasRemainingChildTickets = numRemainingChildTickets > 0;

        if (hasSelectedTickets && hasChildTickets && hasSelectedAdultTicket && hasRemainingChildTickets) {
            return true;
        }

        return false;
    }

    getFareWarnings (fare, trip) {
        const selectedTickets = this.fares.get(fare);
        const remainingTickets = fare.getRefundableTickets().toSet().subtract(selectedTickets);
        const railcards = fare.getRailcards(remainingTickets);
        let warnings = railcards.reduce((result, railcard) => {
            const railcardTickets = fare.getTicketsWithRailCard(railcard, remainingTickets);
            return railcardTickets.isValid() ? result : result.add(`account.orders.refund.warning.${ railcard.code }`);
        }, new Set());

        if (trip.hasPlusBus() && trip.hasFare(fare) && !this.plusBusValid(selectedTickets, trip, fare)) {
            warnings = warnings.add('account.orders.refund.warning.requiresPlusBus');
        }

        if (this.hasChildFlatFareWarning(fare, selectedTickets, remainingTickets)) {
            warnings = warnings.add('account.orders.refund.warning.childFlatFare');
        }

        if (this.hasNotSelectedAllChildren(fare, selectedTickets, remainingTickets)) {
            warnings = warnings.add('account.orders.refund.warning.notAllChildrenAdded');
        }

        return warnings.size ? warnings : null;
    }


    plusBusValid(selectedTickets, trip, fare) {
        if (!!selectedTickets && !fare.isPlusBus()) {
            const tripPlusbus = trip.getProducts().filter((f) => f.isPlusBus() && f.hasRefundableTickets());
            const tripPlusbusTickets = tripPlusbus.map((f) => f.tickets).flatten(1);

            if (tripPlusbus.size <= 0) {
                // there are no refundable plusbus tickets so
                // nothing to worry about
                return true;
            }

            if (!this.plusBus.size > 0) {
                return false;
            }

            const selectedTripPlusbus = trip.getProducts()
                  .map((f) => {
                      if (this.plusBus.has(f)) {
                          return this.plusBus.get(f);
                      }
                      return new Set();
                  }).flatten(1);

            // if there's only plusbus selected, then it's fine
            if (this.plusBus.size > 0 && selectedTickets.size === 0) {
                return true;
            }
            // ticket can have multiple plus bus
            return selectedTripPlusbus.size === tripPlusbusTickets.size;
        }

        return true;
    }

    addTicket (ticket, fare, calculation) {
        let fares;
        if (this.fares.has(fare)) {
            fares = this.fares.set(fare, this.fares.get(fare).add(ticket));
        } else {
            fares = this.fares.set(fare, Set.of(ticket));
        }

        let plusBus = this.plusBus;
        if (fare.isPlusBus() && this.plusBus.has(fare)) {
            plusBus = this.plusBus.set(fare, this.plusBus.get(fare).add(ticket));
        } else if (fare.isPlusBus()) {
            plusBus = this.plusBus.set(fare, Set.of(ticket));
        }

        let { totalUsed } = this;
        if (calculation) {
            totalUsed += calculation.totalUsed;
        }

        return this.set('fares', fares)
            .set('plusBus', plusBus)
            .setTotalUsed(totalUsed);
    }

    removeTicket (ticket, fare, calculation) {
        let fares = this.fares.set(fare, this.fares.get(fare).remove(ticket));
        if (this.fares.get(fare).size === 0) {
            this.fares.remove(fare);

        }

        let plusBus = this.plusBus;
        if (fare.isPlusBus()) {
            plusBus = this.plusBus.set(fare, this.plusBus.get(fare).remove(ticket));

            if (this.plusBus.get(fare).size === 0) {
                this.plusBus.remove(fare);
            }
        }

        let { totalUsed } = this;
        if (calculation) {
            totalUsed -= calculation.totalUsed;
        }

        return this.set('fares', fares)
            .set('plusBus', plusBus)
            .setTotalUsed(totalUsed);
    }

    clearFares () {
        return this.set('fares', new Map());
    }

    setFeePaid () {
        return this.set('feePaid', true);
    }

    setIsSmartcard () {
        return this.set('isSmartcard', true);
    }

    setIsWMC () {
        return this.set('isWMC', true);
    }

    setShouldSurrenderGoldCard () {
        return this.set('shouldSurrenderGoldCard', true);
    }

    setRequestChecks (field, value) {
        return this.set(field, value);
    }

    setTotalUsed (value) {
        return this.set('totalUsed', value);
    }

    setVoucherAmount (amount) {
        let voucherAmount = amount;

        if (!amount || amount < this.minVoucherAmount) {
            voucherAmount = this.minVoucherAmount;
        } else if (amount > this.total) {
            voucherAmount = this.total;
        }

        return this.set('voucherAmount', voucherAmount);
    }

    setGooglePayAmount (amount) {
        return this.set('googlePayAmount', amount);
    }

    setApplePayAmount (amount) {
        return this.set('applePayAmount', amount);
    }

    setBookingFee (value) {
        return this.set('bookingFee', value);
    }

    get tickets() {
        return this.fares.toSet().flatten(true);
    }

    get reasonCode () {
        return this.getIn(['reason', 'code']);
    }

    get subTotal () {
        return sum(this.tickets, 'price') + this.bookingFee;
    }

    get fee () {
        return this.feePaid ? 0 : this.getIn(['reason', 'fee'], 0);
    }

    get total () {
        if (this.isSmartcard) {
            return this.subTotal - this.totalUsed - this.fee - this.bookingFee;
        }
        return this.subTotal - this.fee - this.bookingFee;
    }

    get cardAmount () {
        return this.total - this.voucherAmount;
    }

    get paypalAmount () {
        return this.total - this.voucherAmount;
    }

    get amazonPayAmount () {
        return this.total - this.voucherAmount;
    }

    get googlePayAmount () {
        return this.total - this.voucherAmount;
    }

    get applePayAmount () {
        return this.total - this.voucherAmount;
    }

    get voucherAmountPound () {
        return toPounds(this.voucherAmount);
    }

    get minVoucherAmount () {
        const amount = this.total - this.maxCashReturn;

        return amount > 0 ? amount : 0;
    }
}

Object.assign(RefundRequest.prototype, ValidationMixin);

export default RefundRequest;



// WEBPACK FOOTER //
// ./src/records/refund/Request.js